import type { FC, ReactNode } from 'react'

/**
 * TwoColumnLayout
 * ---------------------------------------------------------------------------
 * Generic two–column layout used across workspace feature pages.
 * The component is intentionally minimal – it only concerns itself with the
 * visual arrangement of a fixed-width left column (typically navigation) and
 * a fluid right column (main content).
 *
 * Consumers decide what to render in each column by passing React nodes via
 * the `left` and `right` props. No additional opinionated markup is rendered
 * – that is left up to the calling route.
 *
 * Widths:
 *  - Left column: 320 px (matches previous WorkspaceContainer implementation)
 *  - Right column: flex-grow to consume remaining space.
 */
export interface TwoColumnLayoutProps {
	/** React node rendered in the fixed-width left column */
	left: ReactNode
	/** React node rendered in the flexible right column */
	right: ReactNode
	/** Optional additional className(s) on the wrapper */
	className?: string
}

export const TwoColumnLayout: FC<TwoColumnLayoutProps> = ({ left, right, className }) => {
	return (
		<div className={`flex flex-1 min-h-0 ${className ?? ''}`}>
			{/* Left / navigation column */}
			<div className="w-[320px] flex flex-col shrink-0">{left}</div>
			{/* Right / main content column */}
			<div className="flex-1 min-h-0">{right}</div>
		</div>
	)
}

export default TwoColumnLayout
